import type { ReactNode } from 'react';
import { formatDisplayPrice } from '@/shared/utils/priceFormatter';
import {
  computeDiscountOnProduct,
  computeGstToShow,
  hasComponentDiscount,
} from '@/features/pdp/utils/priceBreakupUtils';
import { PdpDesktopDetailRow } from './PdpDesktopDetailRow';

interface PdpDesktopPriceBreakupProps {
  goldPrice: number | null;
  diamondPrice: number | null;
  diamondPriceAfterDiscount: number | null;
  discountOnDiamondPriceValue: number | null;
  gemstonePrice: number | null;
  presetSolitairePrice: number | null;
  makingChargePrice: number | null;
  makingChargePriceAfterDiscount: number | null;
  makingChargeDiscountValue: number | null;
  gst: number | null;
  price: number | null;
  discountedPrice: number | null;
  discountValue: number | null;
  flatDiscountPercent: number | null;
  metalType: string;
  gemstonesTypeAndCount: string | null;
}

function parseGemstoneTypes(raw: string | null): Array<{ type: string }> {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Array<{ type?: string }>;
    if (Array.isArray(parsed)) {
      return parsed
        .filter((item) => Boolean(item?.type))
        .map((item) => ({ type: item.type! }));
    }
  } catch {
    // ignore
  }
  return [];
}

function formatPriceValue(value: ReactNode) {
  return <span>{value}</span>;
}

export function PdpDesktopPriceBreakup({
  goldPrice,
  diamondPrice,
  diamondPriceAfterDiscount,
  discountOnDiamondPriceValue,
  gemstonePrice,
  presetSolitairePrice,
  makingChargePrice,
  makingChargePriceAfterDiscount,
  makingChargeDiscountValue,
  gst,
  price,
  discountedPrice,
  discountValue,
  flatDiscountPercent,
  metalType,
  gemstonesTypeAndCount,
}: PdpDesktopPriceBreakupProps) {
  if (!price) {
    return null;
  }

  const discountInput = {
    discountValue,
    makingChargeDiscountValue,
    discountOnDiamondPriceValue,
    makingChargePrice,
    makingChargePriceAfterDiscount,
    diamondPrice,
    diamondPriceAfterDiscount,
  };
  const discountOnProduct = computeDiscountOnProduct(discountInput);
  const gstToShow = computeGstToShow(
    gst,
    flatDiscountPercent,
    discountOnProduct,
    hasComponentDiscount(discountInput)
  );

  const gemTypes = parseGemstoneTypes(gemstonesTypeAndCount);

  return (
    <>
      {goldPrice ? (
        <PdpDesktopDetailRow
          label={metalType || 'Gold'}
          value={formatPriceValue(formatDisplayPrice(goldPrice))}
        />
      ) : null}
      {diamondPrice ? (
        <PdpDesktopDetailRow
          label="Diamond"
          value={formatPriceValue(
            (discountOnDiamondPriceValue ?? 0) > 0 && diamondPriceAfterDiscount !== null
              ? formatDisplayPrice(diamondPriceAfterDiscount)
              : formatDisplayPrice(diamondPrice)
          )}
        />
      ) : null}
      {presetSolitairePrice ? (
        <PdpDesktopDetailRow
          label="Solitaire"
          value={formatPriceValue(formatDisplayPrice(presetSolitairePrice))}
        />
      ) : null}
      {gemstonePrice ? (
        <PdpDesktopDetailRow
          label={gemTypes[0]?.type ?? 'Gemstone'}
          value={formatPriceValue(formatDisplayPrice(gemstonePrice))}
        />
      ) : null}
      {makingChargePrice ? (
        <PdpDesktopDetailRow
          label="Making Charge"
          value={formatPriceValue(
            (makingChargeDiscountValue ?? 0) > 0 && makingChargePriceAfterDiscount !== null
              ? formatDisplayPrice(makingChargePriceAfterDiscount)
              : formatDisplayPrice(makingChargePrice)
          )}
        />
      ) : null}
      {(flatDiscountPercent ?? 0) > 0 && discountOnProduct > 0 ? (
        <PdpDesktopDetailRow
          label="Discount"
          value={formatPriceValue(`- ${formatDisplayPrice(discountOnProduct)}`)}
        />
      ) : null}
      {gstToShow !== null ? (
        <PdpDesktopDetailRow label="GST" value={formatPriceValue(formatDisplayPrice(gstToShow))} />
      ) : null}
      <PdpDesktopDetailRow
        label="Total"
        value={formatPriceValue(
          discountValue && discountedPrice
            ? formatDisplayPrice(discountedPrice)
            : formatDisplayPrice(price)
        )}
      />
    </>
  );
}
